import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../services/apiClient";

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({
    id: "",
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    age: null,
    dob: null,
    profile: null,
    mobileNo: "",
    roles: [],
    lastPasswordChange: null,
    createdAt: "",
    updatedAt: "",
    deactivated: false,
    deleted: false,
  });
  const [loading, setLoading] = useState(true);
  const hasUserDataRef = useRef(false);

  // Fetch user details using cookie-based auth (/user/me)
  const fetchUserDetails = useCallback(async ({ showLoading = true } = {}) => {
    const shouldBlockUI = showLoading && !hasUserDataRef.current;
    if (shouldBlockUI) {
      setLoading(true);
    }
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      
      console.log("UserContext - Fetching user details...");
      console.log("UserContext - Base URL:", baseUrl);

      const response = await apiClient.get(`${baseUrl}/user/me`);

      console.log("UserContext - API Response:", response.data);

      if (response.data.success) {
        const user = response.data.data;
        console.log("UserContext - Setting user data:", user);
        setUserData(user);
        hasUserDataRef.current = Boolean(user?.id);
      } else {
        console.error("UserContext - Failed to fetch user details:", response.data);
      }
    } catch (error) {
      console.error("UserContext - Error fetching user details:", error);
      console.error("UserContext - Error response:", error.response?.data);
    } finally {
      if (shouldBlockUI) {
        setLoading(false);
      }
    }
  }, []);

  // Update user data (called after successful profile update)
  const updateUserData = (newUserData) => {
    setUserData(newUserData);
    hasUserDataRef.current = Boolean(newUserData?.id);
  };

  // Apply auth payload immediately after login / email verification
  const applyAuthResponse = useCallback((authResponse) => {
    if (!authResponse) return;

    const roles = Array.isArray(authResponse.roles)
      ? authResponse.roles
      : authResponse.roles
        ? [...authResponse.roles]
        : [];

    setUserData((prev) => ({
      ...prev,
      id: authResponse.userId || authResponse.id || prev.id,
      email: authResponse.email || prev.email,
      username: authResponse.email || authResponse.username || prev.username,
      firstName: authResponse.firstName || prev.firstName,
      lastName: authResponse.lastName || prev.lastName,
      roles,
      lastPasswordChange: authResponse.lastPasswordChange ?? prev.lastPasswordChange,
    }));
    hasUserDataRef.current = Boolean(authResponse.userId || authResponse.id);
  }, []);

  // Clear user data (called on logout)
  const clearUserData = () => {
    console.log("UserContext - Clearing user data");
    setUserData({
      id: "",
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      age: null,
      dob: null,
      profile: null,
      mobileNo: "",
      roles: [],
      lastPasswordChange: null,
      createdAt: "",
      updatedAt: "",
      deactivated: false,
      deleted: false,
    });
    setLoading(false);
    hasUserDataRef.current = false;
  };

  // Refresh user data without toggling global loading (avoids dashboard mount/unmount loops)
  const refreshUserData = useCallback(() => {
    return fetchUserDetails({ showLoading: false });
  }, [fetchUserDetails]);

  // Force refresh user data after login (called programmatically)
  const forceRefreshUserData = useCallback(() => {
    console.log("UserContext - Force refreshing user data after login");
    return fetchUserDetails({ showLoading: true });
  }, [fetchUserDetails]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // In cookie-based auth, we don't need to listen to localStorage for token changes

  const value = {
    userData,
    loading,
    fetchUserDetails,
    updateUserData,
    applyAuthResponse,
    clearUserData,
    refreshUserData,
    forceRefreshUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}; 